import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ToastTone = 'urgent' | 'action' | 'info' | 'activity';

const TONE: Record<ToastTone, { card: string; tint: string | null; icon: string; bar: string }> = {
  urgent: {
    card: 'border-urgent/40',
    tint: 'bg-urgent/10',
    icon: 'bg-urgent text-urgent-foreground',
    bar: 'bg-urgent',
  },
  action: {
    card: 'border-action/40',
    tint: 'bg-action/10',
    icon: 'bg-action text-action-foreground',
    bar: 'bg-action',
  },
  info: { card: 'border-border', tint: null, icon: 'bg-primary/15 text-primary', bar: 'bg-primary' },
  activity: {
    card: 'border-border',
    tint: null,
    icon: 'bg-muted text-muted-foreground',
    bar: 'bg-muted-foreground/60',
  },
};

interface Props {
  tone: ToastTone;
  icon: LucideIcon;
  title: string;
  detail: string | null;
  time: string | null;
  openLabel?: string;
  onOpen?: () => void;
  onDismiss: () => void;
  durationMs?: number;
}

export function NotificationToastCard({
  tone,
  icon: Icon,
  title,
  detail,
  time,
  openLabel,
  onOpen,
  onDismiss,
  durationMs,
}: Props) {
  const { t } = useTranslation();
  const style = TONE[tone];
  const persistent = !durationMs;
  const labelled = persistent && (tone === 'urgent' || tone === 'action');
  return (
    <div
      data-tone={tone}
      className={cn(
        'relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-card p-3 pr-9 text-card-foreground shadow-lg backdrop-blur',
        style.card
      )}
    >
      {style.tint && (
        <div data-slot="tint" aria-hidden className={cn('pointer-events-none absolute inset-0', style.tint)} />
      )}
      <div className="relative flex gap-3">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', style.icon)}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          {(labelled || time) && (
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
              {labelled && (
                <span className={tone === 'urgent' ? 'text-urgent' : 'text-action'}>
                  {t(`notifications.urgency.${tone}`)}
                </span>
              )}
              {labelled && time && ' · '}
              {time}
            </div>
          )}
          <div className="text-sm font-medium leading-tight">{title}</div>
          {detail && (
            <div data-slot="detail" className="mt-0.5 truncate text-xs text-muted-foreground">
              {detail}
            </div>
          )}
          {onOpen && (
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant={persistent ? 'default' : 'outline'} onClick={onOpen}>
                {openLabel ?? t('notifications.open')}
              </Button>
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label={t('notifications.dismiss')}
        onClick={onDismiss}
        className="absolute right-2 top-2 z-10 rounded p-1 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
      {durationMs && (
        <div
          className={cn('toast-timer absolute inset-x-0 bottom-0 h-0.5', style.bar)}
          style={{ '--toast-duration': `${durationMs}ms` } as CSSProperties}
        />
      )}
    </div>
  );
}
